/**
 * frontend/src/pages/valuation/_valuationIntakeHelpers.ts
 *
 * Pure derivations for ValuationIntake.tsx, split out under
 * docs/FILE_SIZE_POLICY.md ahead of the D2/D3/D4 draft wiring landing on
 * that screen — extracted before the edit rather than after, since the
 * pattern from ValuationTuning.tsx (see _valuationTuningHelpers.ts) is to
 * keep the screen file itself in the safe band, not race the 400-line stop.
 *
 * No React, no state — `buildIntakeBody` is re-run from the screen on
 * submit, it does not own anything itself.
 */
import type { DriverQuestion, RunBody } from '@/types/valuation'
import type { RecomputeBody, TuningBody } from '@/types/valuationB3'
import type { DraftInputs, SaveDraftBody } from '@/types/valuationDraft'
import { buildRecomputeBody } from './_valuationTuningHelpers'

// VAL-INTAKE-BLANK-JUDGMENT-1 — the four rates a valuator must judge start
// blank; years and cap weight keep their defaults.
// VAL-INTAKE-HYDRATE-ALL-1 — defaults exist once, used by both the `useState`
// initialisers below and `startClean()`, so the two can never drift apart.
export const INTAKE_DEFAULTS = {
  riskFree: '',
  equityRiskPremium: '',
  sizePremium: '',
  companySpecificRisk: '',
  longTermGrowth: '',
  baseCashFlow: '',
  replacementCompensation: '',
  growthRate: '',
  // VAL-WIRE-CONSISTENCY-1 R1: blank stays blank — Run is blocked, by name, until typed.
  projectionYears: '',
  capWeight: '',
}

export type HydrateKey = 'risk_free_rate' | 'company_specific_risk' | 'long_term_growth_rate'
  | 'equity_risk_premium' | 'size_premium' | 'growth_rate' | 'projection_years'
  | 'capitalized_earnings_weight' | 'base_cash_flow' | 'replacement_compensation'

/** Blank means "not supplied" — never coerced to 0. */
export function num(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/** The wage is needed unless earnings are known to be at or above the crossover. */
export function replacementCompensationRequired(normalizedEarnings: string, crossover: number): boolean {
  const earnings = num(normalizedEarnings)
  return earnings === null || earnings < crossover
}

/**
 * VAL-ACTOR-JOURNEY-1-SIGNER slice C, R4 — the 5 unconditionally required
 * fields `buildIntakeBody` checks (in the same order), but collecting every
 * miss instead of stopping at the first. `id` matches the `Field` id on
 * ValuationIntake.tsx so the caller can scroll straight to it.
 */
export function getMissingRequiredFields(args: {
  riskFree: string
  companySpecificRisk: string
  longTermGrowth: string
  normalizedEarnings: string
  growthRate: string
  replacementCompensation: string
  crossover: number
  projectionYears: string
  capWeight: string
}): { id: string; label: string }[] {
  const checks: { id: string; label: string; raw: string }[] = [
    { id: 'normalized_earnings', label: 'Normalized earnings', raw: args.normalizedEarnings },
    { id: 'risk_free_rate', label: 'Risk-free rate', raw: args.riskFree },
    { id: 'company_specific_risk', label: 'Company-specific risk', raw: args.companySpecificRisk },
    { id: 'long_term_growth_rate', label: 'Long-term growth rate', raw: args.longTermGrowth },
    { id: 'growth_rate', label: 'DCF growth rate', raw: args.growthRate },
    { id: 'projection_years', label: 'Projection years', raw: args.projectionYears },
    { id: 'capitalized_earnings_weight', label: 'Capitalized-earnings weight', raw: args.capWeight },
  ]
  const missing = checks.filter(c => num(c.raw) === null).map(({ id, label }) => ({ id, label }))
  // VAL-JOURNEY-FIX-2: below the signed SDE/EBITDA crossover, the engine
  // requires replacement owner compensation (core/valuation/income_leg.py:81-91)
  // — list it here too, or the user hits a raw 400 the list didn't warn about.
  // QA-13: blank earnings count as "not yet at the crossover", so it is listed
  // from the first render instead of only after earnings are typed.
  if (replacementCompensationRequired(args.normalizedEarnings, args.crossover) && num(args.replacementCompensation) === null) {
    missing.push({ id: 'replacement_compensation', label: 'Replacement owner compensation' })
  }
  return missing
}

export function buildIntakeBody(args: {
  riskFree: string
  equityRiskPremium: string
  sizePremium: string
  companySpecificRisk: string
  longTermGrowth: string
  normalizedEarnings: string
  replacementCompensation: string
  baseCashFlow: string
  growthRate: string
  projectionYears: string
  capWeight: string
  compMultiples: string
  totalAssets: string
  totalLiabilities: string
  fairValueAdjustments: string
  driverAnswers: Record<string, string>
  questions: DriverQuestion[]
}): RunBody | string {
  const {
    riskFree, equityRiskPremium, sizePremium, companySpecificRisk, longTermGrowth,
    normalizedEarnings, replacementCompensation, baseCashFlow, growthRate, projectionYears, capWeight,
    compMultiples, totalAssets, totalLiabilities, fairValueAdjustments,
    driverAnswers, questions,
  } = args

  const rf = num(riskFree)
  const csr = num(companySpecificRisk)
  const ltg = num(longTermGrowth)
  const earnings = num(normalizedEarnings)
  const growth = num(growthRate)
  if (rf === null) return 'Risk-free rate is required.'
  if (csr === null) return 'Company-specific risk premium is required.'
  if (ltg === null) return 'Long-term growth rate is required.'
  if (earnings === null) return 'Normalized earnings are required.'
  if (growth === null) return 'DCF growth rate is required.'
  const years = num(projectionYears)
  const weight = num(capWeight)
  if (years === null) return 'Projection years is required.'
  if (weight === null) return 'Capitalized-earnings weight is required.'

  // Optional. Omitted, the signed industry profile supplies the market leg
  // (`plan.market_leg` hands the engine [low, anchor, high]), so an empty box
  // is the normal path — not a missing input to be nagged about.
  const comps = compMultiples
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(Number)
  if (comps.some(c => !Number.isFinite(c))) return 'Comparable multiples must be numbers, comma-separated.'

  // VAL-INTAKE-SCOPE-1, ADDENDUM: the driver leg's REQUIRED questions
  // (`question.required`, config data) are mandatory, unconditionally — this
  // mirrors score_questionnaire() itself, which resolves a driver from its
  // required answers alone (S2 fence: corroborating answers never move the
  // score), and a score computed from an incomplete required set is not a
  // defensible one. Corroborating questions never block; they ride along on
  // the wire if answered.
  const requiredQuestions = questions.filter(q => q.required)
  const missingRequired = requiredQuestions.filter(
    q => (driverAnswers[q.question_id] ?? '') === '',
  )
  if (missingRequired.length > 0) {
    return `Answer all ${requiredQuestions.length} required value-driver questions (${missingRequired.length} remaining)`
  }
  const answers: Record<string, string> = {}
  for (const q of questions) {
    const value = driverAnswers[q.question_id]
    if (value) answers[q.question_id] = value
  }

  return {
    risk_free_rate: rf,
    company_specific_risk: csr,
    long_term_growth_rate: ltg,
    equity_risk_premium: num(equityRiskPremium),
    size_premium: num(sizePremium),
    normalized_earnings: earnings,
    // VAL-INCOME-BASIS-FIX-1: blank is allowed here — the engine, not this
    // client, decides whether it's required (400 below the signed crossover).
    replacement_compensation: num(replacementCompensation),
    base_cash_flow: num(baseCashFlow),
    growth_rate: growth,
    projection_years: years,
    capitalized_earnings_weight: weight,
    comp_multiples: comps.length > 0 ? comps : null,
    total_assets: num(totalAssets),
    total_liabilities: num(totalLiabilities),
    fair_value_adjustments: num(fairValueAdjustments),
    driver_answers: Object.keys(answers).length > 0 ? answers : null,
  }
}

/**
 * R6C piece 2 — Intake's Run button now composes the same `/recompute` body
 * Tuning does (`buildRecomputeBody`, `./_valuationTuningHelpers`), instead of
 * posting to `/run`. `useValuationDraft`'s `save()` does not return or update
 * a merged draft, so the merge happens here: the hook's (possibly stale)
 * `draft` state supplies fields this screen does not own this call
 * (`basis`, `override_*`, `naics_code`, `sub_vertical`, and `benchmark_key`
 * when a draft already exists), and the just-validated `body` supplies the
 * numeric fields Intake owns and just saved.
 */
export function buildIntakeRecomputeBody(args: {
  body: RunBody
  driverAnswers: Record<string, string>
  draft: DraftInputs | null
  /** VAL-WIRE-CONSISTENCY-1 R3 — the SAVED tuning, so Intake previews what Methodology will. */
  draftTuning: TuningBody | null
  industryKey: string
  overrideMultiple: string
  overrideRationale: string
  overrideDisposition: string
}): RecomputeBody | null {
  const { body, driverAnswers, draft, draftTuning, industryKey, overrideRationale, overrideDisposition } = args
  // Same rule as `intakeDraftPatch` (`_useIntakeInputs.ts`): a blank multiple
  // means no override, whatever the reason/disposition boxes still hold.
  const overrideMultipleValue = num(args.overrideMultiple)
  const mergedDraft: DraftInputs = {
    benchmark_key: draft?.benchmark_key ?? industryKey,
    normalized_earnings: body.normalized_earnings,
    answers: driverAnswers,
    basis: draft?.basis ?? null,
    income_value: draft?.income_value ?? null,
    total_assets: body.total_assets ?? null,
    total_liabilities: body.total_liabilities ?? null,
    fair_value_adjustments: body.fair_value_adjustments ?? 0, // draft type is a float; draftEngineFields turns 0 back into blank
    // VAL-INPUT-PERSIST-1D — screen wins over draft, same as every other
    // Intake field above: the multiple override now lives on THIS screen,
    // not in the deleted Tuning-era `OverrideSection`.
    override_multiple: overrideMultipleValue,
    override_rationale: overrideMultipleValue === null ? null : (overrideRationale.trim() || null),
    override_disposition: overrideMultipleValue === null
      ? null
      : ((overrideDisposition || null) as DraftInputs['override_disposition']),
    naics_code: draft?.naics_code ?? null,
    sub_vertical: draft?.sub_vertical ?? null,
    market_only_acknowledged: draft?.market_only_acknowledged ?? false,
    risk_free_rate: body.risk_free_rate,
    company_specific_risk: body.company_specific_risk,
    long_term_growth_rate: body.long_term_growth_rate,
    equity_risk_premium: body.equity_risk_premium ?? null,
    size_premium: body.size_premium ?? null,
    growth_rate: body.growth_rate,
    projection_years: body.projection_years ?? null,
    capitalized_earnings_weight: body.capitalized_earnings_weight ?? null,
    base_cash_flow: body.base_cash_flow ?? null,
    replacement_compensation: body.replacement_compensation ?? null,
    // VAL-INPUT-PERSIST-1B B4 — mirrors every other field above: Intake's own
    // just-validated body, not draft state, is authoritative for this call.
    comp_multiples: body.comp_multiples ?? [],
  }
  const built = buildRecomputeBody({
    industry: { key: industryKey, naicsCode: null, subVertical: null },
    earningsOk: true,
    earningsValue: body.normalized_earnings,
    answers: driverAnswers,
    knobs: {},
    baseline: {},
    tuned: false,
    rationale: '',
    draft: mergedDraft,
    acknowledgeMarketOnly: draft?.market_only_acknowledged ?? false,
    compMultiples: body.comp_multiples ?? null,
  })
  if (!built || !draftTuning) return built
  // The saved tuning rides verbatim (it was produced by the same baseline diff
  // Methodology runs), so the two previews agree for the same saved draft.
  return { ...built, tuning: draftTuning, rationale: draftTuning.rationale?.trim() || null }
}

/**
 * VAL-SAVE-ON-TYPE-1A — what autosave sends: every field the Run patch
 * carries (`intakeDraftPatch`, `_useIntakeInputs.ts`), but a blank box is
 * `null` (the user cleared it) while a box that does not parse is OMITTED and
 * named in `problems` — otherwise a typo would erase the saved value.
 * `benchmark_key` is the caller's to add (first save only).
 */
const AUTOSAVE_NUMBERS: Array<[string, keyof IntakeAutosaveArgs, string]> = [
  ['risk_free_rate', 'riskFree', 'Risk-free rate'],
  ['equity_risk_premium', 'equityRiskPremium', 'Equity risk premium'],
  ['size_premium', 'sizePremium', 'Size premium'],
  ['company_specific_risk', 'companySpecificRisk', 'Company-specific risk'],
  ['long_term_growth_rate', 'longTermGrowth', 'Long-term growth rate'],
  ['replacement_compensation', 'replacementCompensation', 'Replacement owner compensation'],
  ['base_cash_flow', 'baseCashFlow', 'Base cash flow'],
  ['growth_rate', 'growthRate', 'DCF growth rate'],
  ['projection_years', 'projectionYears', 'Projection years'],
  ['capitalized_earnings_weight', 'capWeight', 'Capitalized-earnings weight'],
  ['total_assets', 'totalAssets', 'Total assets'],
  ['total_liabilities', 'totalLiabilities', 'Total liabilities'],
]

export interface IntakeAutosaveArgs {
  riskFree: string; equityRiskPremium: string; sizePremium: string; companySpecificRisk: string
  longTermGrowth: string; replacementCompensation: string; baseCashFlow: string; growthRate: string
  projectionYears: string; capWeight: string; totalAssets: string; totalLiabilities: string
  normalizedEarnings: string; fairValueAdjustments: string; compMultiples: string
  driverAnswers: Record<string, string>
  overrideMultiple: string; overrideRationale: string; overrideDisposition: string
  hasDraft?: boolean
}

export function intakeAutosavePatch(a: IntakeAutosaveArgs): { patch: SaveDraftBody; problems: string[] } {
  const patch: Record<string, unknown> = {}
  const problems: string[] = []
  const parsed = (raw: string, label: string): { ok: boolean; value: number | null } => {
    const value = num(raw)
    if (value === null && raw.trim() !== '') { problems.push(label); return { ok: false, value } }
    return { ok: true, value }
  }
  for (const [key, arg, label] of AUTOSAVE_NUMBERS) {
    const r = parsed(a[arg] as string, label)
    if (r.ok) patch[key] = r.value
  }
  const earnings = parsed(a.normalizedEarnings, 'Normalized earnings')
  if (earnings.value !== null) patch.normalized_earnings = earnings.value
  else if (a.hasDraft && a.normalizedEarnings.trim() === '') problems.push('Normalized earnings (required)')
  const adj = parsed(a.fairValueAdjustments, 'Fair-value adjustments')
  if (adj.ok) patch.fair_value_adjustments = adj.value
  const comps = a.compMultiples.split(',').map(s => s.trim()).filter(Boolean).map(Number)
  if (comps.some(c => !Number.isFinite(c))) problems.push('Comparable multiples')
  else patch.comp_multiples = comps.length > 0 ? comps : null
  patch.driver_answers = a.driverAnswers // `{}` clears the saved answers
  const multiple = parsed(a.overrideMultiple, 'Multiple override')
  const reason = a.overrideRationale.trim()
  if (multiple.ok && multiple.value !== null && reason === '') problems.push('Multiple override reason')
  else if (multiple.ok) {
    patch.override_multiple = multiple.value
    patch.override_rationale = multiple.value === null ? null : reason
    patch.override_disposition = multiple.value === null ? null : (a.overrideDisposition || null)
  }
  return { patch: patch as SaveDraftBody, problems }
}
